import { useState } from 'react';
import { Link } from 'react-router-dom';

import type { IntegrationsResponse, OverviewResponse } from '../lib/types';
import { formatCurrency, formatMonth, formatPercent } from '../lib/format';
import {
  costliestCustomers,
  costSummary,
  directCostRows,
  freeTierReading,
  freeTierTone,
  sharedCostRows,
  type CostRow,
} from '../lib/costs';
import {
  ErrorState,
  LoadingState,
  MonthTextPicker,
  PageHeader,
  Section,
  StatCard,
  TableScroll,
  useInsightsQuery,
} from '../components/ui';

const TONE_BAR: Record<ReturnType<typeof freeTierTone>, string> = {
  past: 'bg-red-600',
  close: 'bg-orange-500',
  fine: 'bg-forest',
  unknown: 'bg-stone-200',
};

const TONE_TEXT: Record<ReturnType<typeof freeTierTone>, string> = {
  past: 'text-red-700',
  close: 'text-orange-700',
  fine: 'text-stone-900',
  unknown: 'text-stone-400',
};

function CostTable({ months, rows, total }: { months: string[]; rows: CostRow[]; total: CostRow }) {
  return (
    <TableScroll>
      <table className="w-full min-w-[480px] whitespace-nowrap text-sm">
        <thead>
          <tr className="border-b border-stone-200 text-left text-[11px] uppercase tracking-wider text-stone-400">
            <th className="py-2 font-medium">What</th>
            <th className="py-2 font-medium">Unit cost or kind</th>
            {months.map((m) => (
              <th key={m} className="py-2 pl-4 text-right font-medium">
                {formatMonth(m)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-b border-stone-100">
              <td className="py-2.5 pr-3 font-medium text-stone-800">{row.label}</td>
              <td className="whitespace-normal py-2.5 pr-2 text-xs text-stone-500">{row.note}</td>
              {row.values.map((value, i) => (
                <td key={months[i]} className="tabular py-2.5 pl-4 text-right text-stone-700">
                  {value === null ? <span className="text-stone-300">—</span> : formatCurrency(value)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="font-semibold text-stone-900">
            <td className="pt-2.5">{total.label}</td>
            <td />
            {total.values.map((value, i) => (
              <td key={months[i]} className="tabular pl-4 pt-2.5 text-right">
                {formatCurrency(value)}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </TableScroll>
  );
}

/**
 * Costs (RUT-4073) — what each customer causes and what everyone shares, the
 * unit cost of each direct cost, who costs the most to serve, and which vendor
 * free tiers we are closest to leaving. Served by GET /costs/platform and
 * GET /costs/integrations; the arithmetic is in lib/costs.ts.
 */
export function CostsPage() {
  /** null = follow the latest month. */
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const overview = useInsightsQuery<OverviewResponse>(['overview', 6], '/costs/platform?months=6');
  const integrations = useInsightsQuery<IntegrationsResponse>(['integrations', 6], '/costs/integrations?months=6');

  if (overview.isLoading) return <LoadingState />;
  if (overview.error || !overview.data) return <ErrorState error={overview.error} />;
  const data = overview.data;

  const availableMonths = data.byMonth.map((m) => m.month);
  const latestMonth = availableMonths[availableMonths.length - 1];
  const activeMonth = selectedMonth && availableMonths.includes(selectedMonth) ? selectedMonth : latestMonth;
  const index = data.byMonth.findIndex((m) => m.month === activeMonth);
  const summary = data.byMonth[index];
  const prior = index > 0 ? data.byMonth[index - 1] : null;
  const shown = prior ? [prior, summary] : [summary];
  const shownMonths = shown.map((m) => m.month);
  const isPartial = summary.month === data.partialMonth;
  const monthLabel = formatMonth(summary.month);

  const totals = costSummary(summary, prior);
  const direct = directCostRows(shown);
  const shared = sharedCostRows(
    shown,
    data.otherEnvironments.status,
    data.fixedSaasCosts.filter((s) => s.monthlyCost > 0).map((s) => s.service),
  );
  const costliest = costliestCustomers(data, summary.month);

  return (
    <>
      <PageHeader
        title="Costs"
        subtitle={
          isPartial
            ? `${monthLabel} so far — not final until the month ends`
            : `${monthLabel}, compared with the month before`
        }
        actions={
          <MonthTextPicker
            months={availableMonths}
            value={activeMonth}
            onChange={setSelectedMonth}
            label={activeMonth === latestMonth ? 'Latest month' : 'Summary month'}
          />
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Direct costs"
          value={formatCurrency(totals.direct)}
          hint={`caused by an organization${totals.directEach === null ? '' : ` · ${formatCurrency(totals.directEach)} each`}`}
        />
        <StatCard
          label="Shared costs"
          value={formatCurrency(totals.shared)}
          hint="run whether we have 1 organization or 100"
        />
        <StatCard
          label="Total cost to run"
          value={formatCurrency(totals.total)}
          hint={`${
            isPartial
              ? 'so far this month'
              : totals.change === null
                ? 'no month before to compare'
                : `${totals.change >= 0 ? '+' : ''}${formatPercent(totals.change, 0)} vs ${formatMonth(prior!.month)}`
          }${totals.shareOfRevenue === null ? '' : ` · ${formatPercent(totals.shareOfRevenue, 0)} of revenue`}`}
        />
        <StatCard
          label="Shared cost per organization"
          value={formatCurrency(totals.sharedEach)}
          hint={`across ${summary.countedOrgs} companies this month counts`}
        />
      </div>

      <div className="grid grid-cols-1 gap-x-4 xl:grid-cols-2">
        <Section
          title="Direct costs"
          subtitle="Each one traces to the company that caused it. Subscription margin takes off only these."
        >
          <CostTable months={shownMonths} rows={direct.rows} total={direct.total} />
          <p className="mt-3 text-xs leading-relaxed text-stone-500">
            Texts are Twilio’s per-message charges. Visit processing is about 1¢ of AWS per visit. Crew GPS is
            DynamoDB’s write charges — almost all crew location writes — split by the GPS segments each company wrote (by crew hours for months past the location table’s 90 days).
          </p>
        </Section>

        <Section title="Shared costs" subtitle="No single company causes these. Only overall margin includes them.">
          <CostTable months={shownMonths} rows={shared.rows} total={shared.total} />
          <p className="mt-3 rounded-lg border border-stone-100 bg-stone-50 px-3 py-2 text-xs leading-relaxed text-stone-600">
            Payment processing is not a cost on this page. JustiFi’s fees are neither our revenue nor our cost — Ruta
            Pay counts only as the spread we keep, on{' '}
            <Link to="/" className="text-primary hover:underline">
              Home
            </Link>
            .
          </p>
        </Section>
      </div>

      <div className="grid grid-cols-1 gap-x-4 xl:grid-cols-2">
        <Section title="Free tiers we are close to leaving" subtitle="Where shared costs step up next">
          {integrations.isLoading ? (
            <LoadingState />
          ) : integrations.error || !integrations.data ? (
            <ErrorState error={integrations.error} />
          ) : (
            <ul className="flex flex-col gap-4">
              {integrations.data.freeTiers.map((item) => {
                const tone = freeTierTone(item);
                const reading = freeTierReading(item);
                return (
                  <li key={item.id}>
                    <div className="flex items-baseline justify-between gap-3 text-sm">
                      <span className="font-medium text-stone-800">{item.name}</span>
                      <span className={`tabular shrink-0 text-right font-semibold ${TONE_TEXT[tone]}`}>
                        {reading.headline}
                      </span>
                    </div>
                    <div className="mt-1.5 h-2 rounded-full bg-stone-100">
                      {item.usedShare !== null && (
                        <div
                          className={`h-2 rounded-full ${TONE_BAR[tone]}`}
                          style={{
                            width: `${Math.min(100, Math.max(item.usedShare * 100, item.usedShare > 0 ? 2 : 0))}%`,
                          }}
                        />
                      )}
                    </div>
                    <p className="mt-1.5 text-xs text-stone-500">
                      {reading.detail} Next step: {item.nextStep}.
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </Section>

        <Section
          title={`Highest direct cost, ${monthLabel}`}
          subtitle="Who costs the most to serve, next to what they bring in"
        >
          {costliest.length === 0 ? (
            <p className="text-sm text-stone-500">No company this month counts caused a direct cost yet.</p>
          ) : (
            <TableScroll>
              <table className="w-full min-w-[520px] whitespace-nowrap text-sm">
                <thead>
                  <tr className="border-b border-stone-200 text-left text-[11px] uppercase tracking-wider text-stone-400">
                    <th className="py-2 font-medium">Company</th>
                    <th className="py-2 font-medium">Biggest driver</th>
                    <th className="py-2 pl-4 text-right font-medium">Direct cost</th>
                    <th className="py-2 pl-4 text-right font-medium">Revenue to Ruta</th>
                    <th className="py-2 pl-4 text-right font-medium">Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {costliest.map((row) => (
                    <tr key={row.orgId} className="border-b border-stone-100">
                      <td className="py-2.5 font-medium text-stone-800">{row.name}</td>
                      <td className="py-2.5 text-xs text-stone-500">{row.driver}</td>
                      <td className="tabular py-2.5 pl-4 text-right text-stone-900">
                        {formatCurrency(row.directCost)}
                      </td>
                      <td className="tabular py-2.5 pl-4 text-right text-stone-700">
                        {formatCurrency(row.revenueToRuta)}
                      </td>
                      <td className="tabular py-2.5 pl-4 text-right text-stone-700">
                        {formatPercent(row.marginPct, 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
          <p className="mt-3 text-xs text-stone-400">
            Margin here is subscription margin: revenue less only what that company caused.
          </p>
        </Section>
      </div>
    </>
  );
}
